import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDPagination from 'components/MDPagination';
import Icon from '@mui/material/Icon';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { apiGetGrindPointHist } from '../../api/stats.api';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { handleDownloadExcel } from '../../components/excel/excelUtils';
import MDButton from '../../components/MDButton';
import { MenuItem, Select, InputLabel, FormControl, Snackbar, Alert } from '@mui/material';

const Points = () => {
  const [grinds, setGrinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [branchId, setBranchId] = useState(null);
  const [machineId, setMachineId] = useState(null);
  const [userId, setUserId] = useState('');
  const [method, setMethod] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { user, appIsReady } = useData();

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setCreatedFrom(today);
    setCreatedTo(today);
  }, []);

  useEffect(() => {
    if (createdFrom && createdTo && appIsReady && user) {
      fetchGrinds(page);
    }
  }, [page, user, appIsReady, createdFrom, createdTo]);

  const fetchGrinds = async (pageNum) => {
    setLoading(true);
    try {
      const response = await apiGetGrindPointHist(pageNum, 10, branchId, machineId, createdFrom, createdTo, userId, method, user.accessToken);
      if (response && response.data) {
        setGrinds(response.data.data);
        setTotal(response.data.total);
      } else {
        console.log('No data received');
      }
    } catch (error) {
      console.error('Failed to load grinds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
  };

  const handleSearch = () => {
    fetchGrinds(1);
    setPage(1);
  };

  const handleInputChange = (e, setter) => {
    const value = e.target.value;
    if (value === '' || /^[0-9]+$/.test(value)) {
      setter(Number(value));
      setErrorMessage('');
    } else {
      setErrorMessage('숫자만 입력할 수 있습니다.');
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit', 
      hour12: false 
    };
    return new Date(dateString).toLocaleString('ko-KR', options).replace(/. /g, '-').replace('.', '').replace(' ', ' ');
  };

  const rows = grinds.map(pointhist => ({
    id: pointhist.id,
    branch_name: pointhist.branch?.branch_name ?? '',
    machine_id: pointhist.machine_id,
    device_name: pointhist.petmachine?.device_name ?? '',
    userid: pointhist.userid,
    rpoint: pointhist.rpoint,
    mpoint: pointhist.mpoint,
    method: pointhist.method === 'u' ? '적립' : pointhist.method === 'm' ? '관리자부여' : pointhist.method ==='change' ? '전환' : pointhist.method === 'transfer' ? '전송' : '' ,
    detail : pointhist.detail,
    createdAt: formatDate(pointhist.createdAt),
  }));

  const columns = [
    { Header: "ID", accessor: "id", align: "left" },
    { Header: "지점명", accessor: "branch_name", align: "left" },
    { Header: "Machine ID", accessor: "machine_id", align: "left" },
    { Header: "기기명", accessor: "device_name", align: "left" },
    { Header: "사용자ID", accessor: "userid", align: "left" },
    { Header: "R Point", accessor: "rpoint", align: "left" },
    { Header: "M Point", accessor: "mpoint", align: "left" },
    { Header: "종류", accessor: "method", align: "left" },
    { Header: "내용", accessor: "detail", align: "left" },
    { Header: "일시", accessor: "createdAt", align: "left" },
  ];

  const totalPages = Math.ceil(total / 10);
  const maxPageNumbers = 10;
  const currentPageGroup = Math.floor((page - 1) / maxPageNumbers);
  const startPage = currentPageGroup * maxPageNumbers + 1;
  const endPage = Math.min(startPage + maxPageNumbers - 1, totalPages);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);

  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton variant="gradient" color="info" onClick={() => handleDownloadExcel('포인트내역', columns, rows)} sx={{ ml: 2 }}>엑셀 다운로드</MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  포인트
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={1}>
                        <TextField
                          label="Branch ID"
                          fullWidth
                          value={branchId ?? ''}
                          onChange={(e) => handleInputChange(e, setBranchId)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={1}>
                        <TextField
                          label="Machine ID"
                          fullWidth
                          value={machineId ?? ''}
                          onChange={(e) => handleInputChange(e, setMachineId)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={1}>
                        <TextField
                          label="User ID"
                          fullWidth
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={1}>
                        <FormControl fullWidth>
                          <InputLabel id="method-label">종류</InputLabel>
                          <Select
                            fullWidth
                            label="종류"
                            labelId="userType-label"
                            name="method"
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                            variants='solid'
                            sx={{
                              height: 45,
                            }}
                          >
                            <MenuItem value="m">관리자</MenuItem>
                            <MenuItem value="u">적립</MenuItem>
                            <MenuItem value="change">전환</MenuItem>
                            <MenuItem value="transfer">전송</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={1.5}>
                        <TextField
                          label="분쇄일 From"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdFrom}
                          onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={1.5}>
                        <TextField
                          label="분쇄일 To"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdTo}
                          onChange={(e) => setCreatedTo(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={handleSearch}
                        >
                          검색
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={3}
              >
                <MDPagination
                  variant="gradient"
                  color="info"
                >
                  {page > 1 && (
                    <MDPagination item onClick={() => handlePageChange(page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {pageNumbers.map(pageNum => (
                    <MDPagination
                      item
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      active={page === pageNum}
                    >
                      {pageNum}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination item onClick={() => handlePageChange(page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Points;
